<template>
  <template v-if="navigation.items && navigation.items.length">
    <a
      :class="{ 'bg-gray-50': isOpen }"
      class="flex flex-col justify-center items-center lg:h-full text-center text-13 font-bold rounded-t-6 px-5 hover:bg-gray-50"
      :href="navigation.link.link ?? undefined"
      @click="trackItemClick"
      @mouseenter="onItemMouseEnter"
      @mouseleave="onItemMouseLeave"
      @keydown.space.prevent="$emit('hover-item', navigation)"
      @keydown.esc="$emit('close')"
      role="button"
      :aria-expanded="isOpen"
    >
      <img
        v-if="navigation.link.icon?.url"
        class="h-5 w-5"
        :src="navigation.link.icon.url"
        alt=""
      />
      <span class="pt-2">{{ navigation.link.label }}</span>
    </a>
    <div
      v-show="isOpen"
      class="absolute inset-x-0 w-full z-20 top-24"
      @mouseleave="emit('close')"
      @mouseover="emit('hover-item-content')"
    >
      <div class="flex max-h-112 bg-gray-50 shadow-2">
        <div
          class="grid py-8 pl-48 w-full max-w-screen-2xl 2xl:mx-auto"
          :class="navHighlightsAvailable || showMenuCta ? 'grid-cols-3' : 'grid-cols-2'"
        >
          <div v-if="navHighlightsAvailable || showMenuCta" class="px-4">
            <ul v-if="navHighlightsAvailable">
              <li v-for="highlight in navigation.highlights" :key="`highlight-${highlight.label}`">
                <MenuItem :item="highlight" location="header" is-desktop />
              </li>
            </ul>
            <MenuCta v-if="showMenuCta" class="py-4 mx-7 xl:mx-1" />
          </div>

          <ul class="border-r-2 border-gray-200 px-4 overflow-auto">
            <li v-for="(item, index) in navigation.items" :key="`level-2-item-${index}`">
              <MenuItem
                :item="item.link"
                :has-items="Boolean(item.items?.length)"
                location="header"
                @enter="openThirdLevel(item)"
                @open="openThirdLevel(item)"
                is-desktop
              />
            </li>
          </ul>

          <template v-for="(item, index) in navigation.items" :key="`level-3-menu-${index}`">
            <div
              v-if="item.items?.length"
              v-show="item.link.label === openItemLabel"
              class="px-4 overflow-auto"
            >
              <div class="hidden">{{ item.link.label }}</div>
              <ul>
                <li v-for="(item2, index2) in item.items" :key="`level-3-item-${index2}`">
                  <MenuItem :item="item2" location="header" is-desktop />
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </div>
  </template>
  <a
    v-else
    class="flex flex-col items-center justify-center text-13 px-4 lg:h-full hover:bg-gray-50 rounded-t-6"
    :href="navigation.link.link ?? undefined"
    @click="trackItemClick"
    @mouseenter="$emit('close')"
  >
    <img v-if="navigation.link.icon?.url" class="h-5 w-5" :src="navigation.link.icon.url" alt="" />
    <span class="font-bold pt-2">{{ navigation.link.label }}</span>
  </a>
</template>

<script lang="ts" setup>
import type { GQLNavigationItem } from './generated/page-header'
import { NavigationItemClick, NavigationMenuOpen } from '@core/analytics/core-events/navigation'
import MenuItem from './menu-item.vue'
import MenuCta from './menu-cta.vue'

const props = defineProps<{
  navigation: GQLNavigationItem
  isOpen: boolean
}>()

const emit = defineEmits(['hover-item', 'leave-item', 'hover-item-content', 'close'])

const { $track } = useNuxtApp()

const openItemLabel = ref<string | null>(null)

const showMenuCta = computed(() => props.navigation.id === 'money-transfer-comparison')

const navHighlightsAvailable = computed(() => {
  return Boolean(props.navigation.highlights?.length)
})

function onItemMouseEnter() {
  emit('hover-item', props.navigation)
  openItemLabel.value = null
}

function onItemMouseLeave() {
  emit('leave-item', props.navigation)
}

async function openThirdLevel(item: NonNullable<GQLNavigationItem['items']>[number]) {
  if (item.items?.length && openItemLabel.value !== item.link.label) {
    openItemLabel.value = item.link.label
    await $track(NavigationMenuOpen(item.link.label))
  }
}

async function trackItemClick() {
  await $track(NavigationItemClick(props.navigation.link.link, 'header'))
}
</script>
